import { useMemo, useState } from "react";
import { GetListResponse, useCustomMutation, useNavigation, useNotification } from "@refinedev/core";
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Typography, Box, Stack, IconButton, MenuItem, Menu } from "@mui/material";
import { DeleteButton, EditButton, ShowButton } from "@refinedev/mui";
import { IContact } from "@/utils/zod/contactFormValidation";
import CustomChip from "@/components/override/Chip";
import { MoreVert, Replay } from "@mui/icons-material";
import { useTableState } from "@/hooks/useTableState";
import { useLocation } from "react-router";

type Props = {
  tableQuery: {
    data: GetListResponse<IContact>;
    isLoading: boolean;
    isError: boolean;
  };
};

export const ContactListTable = ({ tableQuery }: Props) => {
  const { data, isLoading, isError } = tableQuery;
  const navigate = useNavigation();
  const { search } = useLocation();
  const { tableState, onColumnFiltersChange, onGlobalFilterChange, onSortingChange } = useTableState<IContact>();

  const [menuState, setMenuState] = useState<{ anchorEl: HTMLElement | null; rowId: string | null }>({
    anchorEl: null,
    rowId: null,
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
    setMenuState({
      anchorEl: event.currentTarget,
      rowId: rowId,
    });
  };

  const handleMenuClose = () => {
    setMenuState({
      anchorEl: null,
      rowId: null,
    });
  };

  const { mutate: resetDeviceId } = useCustomMutation();
  const { open } = useNotification();

  const columns = useMemo<MRT_ColumnDef<IContact>[]>(
    () => [
      {
        accessorKey: 'id',
        header: '#id',
        Cell: ({ row }) => row.index + 1,
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'name',
        header: 'Name',
        size: 140,
        accessorFn: (row) => row.name || row.user?.name || '',
        Cell: ({ row }) => (
          <Typography>
            {(row.original.name && row.original.user?.name) ? `${row.original.name} (${row.original.user?.name})` : row.original.name ? row.original.name : row.original.user?.name ? `(${row.original.user?.name})` : ''}
          </Typography>
        ),
      },
      {
        accessorKey: 'user.phone_number',
        header: 'Phone',
        size: 120,
        Cell: ({ row }) => (
          <Typography>
            {row.original.user?.phone_number?.toString() ?? ''}
          </Typography>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'company',
        header: 'Company',
        size: 200,
        Cell: ({ row }) => {
          const company = row.original.company_ledger_name || "";

          return (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {company && <CustomChip
                label={company}
                status="neutral"
                type="company"
                size="small"
              />}
            </Box>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        Cell: ({ row }) => (
          <CustomChip
            label={row.original.status}
            status={row.original.status === "approved" ? "positive" : row.original.status === "banned" ? "negative" : "neutral"}
            type="status"
            size="small"
          />
        ),
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        size: 254,
        Cell: ({ row }) => {
          const text = row.original.user?.description ?? row.original.notes ?? '';
          return (
            <Typography
              sx={{
                whiteSpace: "pre-wrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
              }}
            >
              {text}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const rowId = row.original.id?.toString() ?? '';
          
          return (
            <Stack direction="row" spacing={1}>
              <EditButton 
                hideText 
                resource="accounts" 
                recordItemId={rowId}
                meta={{
                  to: `/accounts/${rowId}/edit`,
                  query: Object.fromEntries(new URLSearchParams(search))
                }}
              />
              <DeleteButton hideText resource="accounts" recordItemId={rowId} />
              <IconButton onClick={(e) => handleMenuClick(e, rowId)}>
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={menuState.rowId === rowId ? menuState.anchorEl : null}
                open={menuState.rowId === rowId && !!menuState.anchorEl}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => navigate.show("accounts", rowId)}>
                  <ShowButton resource="accounts" recordItemId={rowId} sx={{ backgroundColor: 'transparent' }}/>
                </MenuItem>

                <MenuItem onClick={() => {
                  resetDeviceId({
                    url: `/accounts/reset-device-id/${rowId}`,
                    method: "patch",
                    values: {},
                  }, {
                    onSuccess: () => {
                      open?.({
                        message: "Device ID reset successfully",
                        type: "success",
                      });
                    },
                    onError: () => {
                      open?.({
                        message: "Failed to reset device ID",
                        type: "error",
                      });
                    },
                  })
                  handleMenuClose();
                }}>
                  <Replay fontSize="small" sx={{ mr: 1 }} />
                  Reset Device ID
                </MenuItem>
              </Menu>
            </Stack>
          );
        },
      },
    ],
    [search, menuState],
  );

  const sortedContacts = data.data
    .sort((a, b) => {
      const dateA = new Date(a.inserted_at).getTime();
      const dateB = new Date(b.inserted_at).getTime();
      return dateB - dateA; // Descending order (newest first)
    });

  return (
    <MaterialReactTable
      columns={columns}
      data={sortedContacts ?? []}
      enableTopToolbar
      enableBottomToolbar
      enableColumnFilters
      enableGlobalFilter
      enablePagination
      muiTableBodyRowProps={{ hover: true }}
      muiPaginationProps={{
        rowsPerPageOptions: [1000],
        showFirstButton: true,
        showLastButton: true,
      }}
      state={{
        ...tableState,
        isLoading,
        pagination: {
          pageSize: 1000,
          pageIndex: 0,
        },
      }}
      onColumnFiltersChange={onColumnFiltersChange}
      onGlobalFilterChange={onGlobalFilterChange}
      onSortingChange={onSortingChange}
      muiToolbarAlertBannerProps={
        isError
          ? {
            color: 'error',
            children: 'Error loading data',
          }
          : undefined
      }
      enableColumnResizing
      enableColumnPinning
      enableFullScreenToggle
      enableDensityToggle
      enableGlobalFilterModes
      positionGlobalFilter="right"
      getRowId={(row) => row.id?.toString() ?? ''}
    />
  );
}; 